/**
 * RecentFiles — Tracks recently opened/saved maps in localStorage.
 *
 * Stores file name, map dimensions, timestamp, and optionally the TMJ data
 * itself (for maps that don't have a file handle — i.e., most browser usage).
 *
 * Max 10 entries, newest first. Deduplicates by filename.
 */

const STORAGE_KEY = 'tileforge_recent_files';
const MAX_ENTRIES = 10;
// Max TMJ snapshot size to store (100KB) — larger maps just store metadata
const MAX_SNAPSHOT_SIZE = 100 * 1024;

export class RecentFiles {
  constructor() {
    this._entries = this._load();
  }

  /**
   * Record a map open/save.
   * @param {string} fileName
   * @param {Object} info — { width, height, layerCount, tilesets }
   * @param {string} [tmjSnapshot] — optional TMJ JSON string for quick reopen
   */
  add(fileName, info, tmjSnapshot = null) {
    if (!fileName || fileName === 'untitled.tmj') return;

    // Remove existing entry with same name
    this._entries = this._entries.filter(e => e.fileName !== fileName);

    const entry = {
      fileName,
      width: info.width || 0,
      height: info.height || 0,
      layerCount: info.layerCount || 0,
      tilesetNames: (info.tilesets || []).map(ts => ts.name).slice(0, 3),
      timestamp: Date.now(),
    };

    // Store TMJ snapshot if small enough for quick reopen
    if (tmjSnapshot && tmjSnapshot.length <= MAX_SNAPSHOT_SIZE) {
      entry.snapshot = tmjSnapshot;
    }

    this._entries.unshift(entry);
    this._entries = this._entries.slice(0, MAX_ENTRIES);
    this._save();
  }

  /** Get recent entries (newest first). */
  list() {
    return this._entries;
  }

  /** Clear all recent entries. */
  clear() {
    this._entries = [];
    this._save();
  }

  /** Remove a specific entry. */
  remove(fileName) {
    this._entries = this._entries.filter(e => e.fileName !== fileName);
    this._save();
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch {
      return [];
    }
  }

  _save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._entries));
    } catch {
      // localStorage full — drop snapshots and retry
      for (const e of this._entries) delete e.snapshot;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this._entries));
      } catch { /* give up */ }
    }
  }
}
